import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SortDownArrow } from "../../../../assets/svg";
import Action from "../../../../components/common/Action";
import { DefaultPrimaryButton } from "../../../../components/micro/input";
import DebounceSearch from "../../../../components/common/DebounceSearch";
import RippleLoader from "../../../../components/loader/RippleLoader";
import CustomTable from "../../../../components/customTable/CustomTable";
import ConfirmationModel from "../../../../components/common/ConfirmationModel";
import { DeleteOutlet, getOutlet } from "../../../../redux/companies/slice";

const Outlet = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storedData = sessionStorage.getItem("companyData");
  const companyData = storedData ? JSON.parse(storedData)?.company : null;
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [productDelete, setProductDelete] = useState(null);

  // Selector
  const { outletList, loading } = useSelector((store) => ({
    outletList: store?.companiesData?.getOutletStatus?.data?.data,
    loading: store?.companiesData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getOutlet({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        company_id: companyData?.id || "",
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  const handleOutletExportClick = () => {
    if (!outletList?.data || outletList?.data.length === 0) {
      alert("No data available to export!");
      return;
    }

    const header = [
      "Outlet Name",
      "Address",
      "Technician",
      "No. of Equipment",
      "No. of Staff",
      "Operation Time",
      "Time to Avoid",
    ];

    const rows = outletList?.data.map((outlet) => [
      `"${outlet?.name || "-"}"`,
      `"${outlet?.address || "-"}"`,
      `"${
        outlet?.technicians?.length > 0 ? outlet?.technicians.join(", ") : "-"
      }"`,
      `"${outlet?.equipment_count || "-"}"`,
      `"${outlet?.staff_count || "-"}"`,
      `"${outlet?.operation_time || "-"}"`,
      `"${
        outlet?.avoid_times
          ?.map((time) => `${time?.start_time} - ${time?.end_time}`)
          .join(", ") || "-"
      }"`,
    ]);

    let csvContent = header.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "outlets_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const deleteOutletId = () => {
    setIsLoading(true);
    dispatch(DeleteOutlet(productDelete))
      .then((res) => {
        if (res.type === "DeleteOutlet/fulfilled") {
          dispatch(
            getOutlet({
              search: searchValue,
              pageCount: pageCount,
              pageLimit: pageLimit,
              company_id: companyData?.id || "",
              sort_by: sortBy,
              sort_direction: sortDirection === "asc" ? "asc" : "desc",
            })
          );
        }
        setConfirmDelete(false);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error deleting remark:", error);
        setIsLoading(false);
        setConfirmDelete(false);
      });
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        No.
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Outlet Name</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "name" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        Address
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("technician");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Technician</span>
          <SortDownArrow
            className={`ml-1 transition-transform ${
              sortDirection === "asc" && sortBy === "technician"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("equipment_count");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span> No. of Equipment </span>
          <SortDownArrow
            className={`ml-1 transition-transform ${
              sortDirection === "asc" && sortBy === "equipment_count"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        No. of Staff
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Operation Time
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Time to Avoid
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Actions
      </th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(outletList?.data || [])?.map((element, index) => (
        <tr
          key={index}
          className="font-Proxima shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-5 py-3 truncate">{index + 1}</td>
          <td className="px-5 py-3 truncate">{element?.name}</td>
          <td className="px-5 py-3 truncate">{element?.address}</td>
          <td className="px-5 py-3 truncate">
            <div className="truncate-lines-3">
              {element?.technicians?.length > 0 ? (
                element.technicians.map((tech, idx) => (
                  <div key={idx}>{tech?.length ? tech : "-"}</div>
                ))
              ) : (
                <div>-</div>
              )}
            </div>
          </td>
          <td className="px-5 py-3 truncate">{element?.equipment_count}</td>
          <td className="px-5 py-3 truncate">{element?.staff_count}</td>
          <td className="px-5 py-3">{element?.operation_time}</td>
          <td className="px-5 py-3">
            {element?.avoid_times?.map((time, index) => (
              <div key={index}>
                {time?.start_time} - {time?.end_time}
              </div>
            ))}
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/create-edit-outlet", {
                        state: { outlet: element, mode: "edit" },
                      })
                    }
                  >
                    View/Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      navigate("/create-edit-equipment", {
                        state: { outlet: element, mode: "editOutlet" },
                      });
                    }}
                  >
                    Add Equipment
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setProductDelete(element?.id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-5  w-full bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <div>
            <DefaultPrimaryButton
              value="EXPORT"
              height={"42px"}
              onClick={handleOutletExportClick}
            />
          </div>
        </div>
        {loading ? (
          <RippleLoader />
        ) : (
          <div>
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={outletList}
              overflowVisibleLg={true}
              // loader
              loading={false}
              showPagination={true}
              // showLimit
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              // paginationData
              total={outletList?.total}
              lastPage={Math.ceil(outletList?.total / pageLimit)}
              current={pageCount}
              paginationData={(crPage) => setPageCount(crPage)}
            />
          </div>
        )}
      </div>

      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteOutletId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default Outlet;
